import { localizedStrings } from '@core/strings'
import React from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Box, Flex, Text } from '@fundamentals'
import {
  Checkbox,
  DialogTitle,
  IconButton,
  Button,
  Dialog,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import _xor from 'lodash/xor'
import { ChecklistCard } from '../Cards'
import { useAllOrganisationChecklists } from '@core/react-query/features/checklists'
import { Checklist, ChecklistStatus } from '@core/entities/checklist/Checklist'
import { useChecklistFilters } from '@common/hooks/useChecklistFilters'
import { Filters } from '@elements/Filters'

type SelectOrganisationChecklistsModalProps = {
  title: string
  triggerComponent: (onClick) => React.ReactNode
  value?: Array<number>
  onChange?: (value: Array<number>, valueObjects: Array<Checklist>) => void
  organisationId: number
  filters?: {
    statuses?: Array<ChecklistStatus>
    templateIds?: Array<number>
    projectIds?: Array<number>
    isUsedForInspections?: boolean
  }
  pageSize?: number
  readOnly?: boolean
  multiSelect?: boolean
}

export const SelectOrganisationChecklistsModal: React.FC<
  SelectOrganisationChecklistsModalProps
> = ({
  value = [],
  onChange,
  title,
  organisationId,
  filters,
  pageSize = 25,
  readOnly,
  multiSelect,
  triggerComponent,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const scope = {
    type: 'ORGANISATION' as const,
    organisationId,
  }

  const {
    filters: filterOptions,
    payload,
    queryParamsStore,
  } = useChecklistFilters(scope, organisationId, {
    templateFilterProps: { whitelist: filters?.templateIds, readOnly: true },
    projectFilterProps: { whitelist: filters?.projectIds, readOnly: true },
    isUsedForInspections: filters?.isUsedForInspections,
    statuses: filters?.statuses,
  })

  const { data, hasNextPage, fetchNextPage, isLoading } =
    useAllOrganisationChecklists({
      organisationId,
      ...payload,
      size: pageSize,
    })

  const numberOfChecklists =
    (data?.pages.length - 1) * pageSize + data?.pages.slice(-1)[0].length

  const selectChecklist = (checklistId: number) => {
    const newValue = multiSelect ? _xor(value, [checklistId]) : [checklistId]
    const newValueData = newValue.map((id) => {
      for (const page of data.pages) {
        const permit = page.find((p) => p.id === id)
        if (permit) return permit
      }
    })
    onChange(newValue, newValueData)
  }

  return (
    <>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullWidth>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20, mb: 0 }}
        >
          {title}
          <IconButton aria-label='close-modal' onClick={() => setIsOpen(false)}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <Box px={2} mt={2}>
          <Filters filters={filterOptions} store={queryParamsStore} isInModal />
        </Box>
        <Box
          id='scrollableContainer'
          overflow='auto'
          p={2}
          pt={0}
          height='800px'
        >
          {!isLoading && (
            <InfiniteScroll
              dataLength={numberOfChecklists}
              next={fetchNextPage}
              hasMore={hasNextPage}
              loader={
                <Text mt={1.5} textAlign='center' color='grey'>
                  {localizedStrings.loading}
                </Text>
              }
              endMessage={
                <Text mt={1.5} textAlign='center' color='grey'>
                  {localizedStrings.noMoreResults}
                </Text>
              }
              scrollableTarget='scrollableContainer'
            >
              {data?.pages.flatMap((page) => {
                return page.map(({ id, ...permit }) => {
                  const isSelected = value.includes(id)
                  return (
                    <ChecklistCard
                      key={id}
                      templateName={permit.template.name}
                      sx={{
                        borderColor: isSelected ? 'primary.main' : null,
                        '&:hover': { borderColor: 'primary.light' },
                        mt: 2,
                      }}
                      onClick={() => selectChecklist(id)}
                      icon={
                        <Checkbox
                          checked={isSelected}
                          onChange={() => selectChecklist(id)}
                          disabled={readOnly}
                        />
                      }
                      {...permit}
                    />
                  )
                })
              })}
            </InfiniteScroll>
          )}
          <Flex width='100%' justifyContent='flex-end' mt={3}>
            <Box
              position='absolute'
              bottom={0}
              width='100%'
              bgcolor='white'
              py={2}
            >
              <Button
                variant='contained'
                sx={{ float: 'right', zIndex: 10 }}
                onClick={() => setIsOpen(false)}
              >
                {localizedStrings.done}
              </Button>
            </Box>
          </Flex>
        </Box>
      </Dialog>
      {triggerComponent(() => setIsOpen(true))}
    </>
  )
}
